/**
 * signal-reco.service.ts — Reco deterministe PARTAGEE d'un signal Juliette.
 *
 * Source de verite unique de la phrase « pourquoi relancer » d'un signal et de
 * la selection du signal de relance d'un contact. Reutilisee a deux endroits
 * pour garantir la COHERENCE : la liste des signaux (dashboard + panneau) et la
 * carte « Juliette recommande » de la fiche contact affichent EXACTEMENT le meme
 * texte pour un meme contact.
 *
 * 100 % deterministe (zero invention, zero I/O) : reutilise buildExchangeSegment
 * (contact-context.service) et l'eligibilite relance (juliette-relance-instructions).
 */

import { canPrepareRelance } from "@/lib/juliette-relance-instructions";
import {
  buildExchangeSegment,
  type LastExchangeSummary,
} from "@/lib/services/contact-context.service";
import type { JulietteSignal } from "@/lib/juliette-signals";

/** Rang de tri par severite (urgent d'abord) — meme bareme que juliette-signals/whisper. */
const SEVERITY_RANK: Record<string, number> = { urgent: 0, warning: 1, info: 2 };

/**
 * LE signal de relance le plus urgent pour ce contact : pending, eligible a une
 * pre-redaction (`canPrepareRelance`), trie par severite puis fraicheur. C'est ce
 * signal qui pilote la carte « Juliette recommande » de la fiche. `undefined` si
 * le contact n'a aucun signal de relance en attente.
 */
export function relanceSignalForContact(
  signals: JulietteSignal[],
  contactId: string,
): JulietteSignal | undefined {
  return [...signals]
    .filter((s) => s.contactId === contactId && canPrepareRelance(s.signalType))
    .sort(
      (a, b) =>
        (SEVERITY_RANK[a.severity] ?? 9) - (SEVERITY_RANK[b.severity] ?? 9) ||
        b.detectedAt.localeCompare(a.detectedAt),
    )[0];
}

/** Nombre de jours de silence porte par le signal (payload.days_silent), ou null. */
function silentDays(signal: JulietteSignal): number | null {
  const raw = signal.payload?.days_silent;
  return typeof raw === "number" && Number.isFinite(raw) && raw >= 0 ? Math.round(raw) : null;
}

/** Anciennete lisible : « depuis 1 jour » / « depuis 12 jours » / « depuis 2 mois ». */
function formatSilence(days: number): string {
  if (days < 2) return "depuis 1 jour";
  if (days < 30) return `depuis ${days} jours`;
  const months = Math.round(days / 30);
  return months <= 1 ? "depuis 1 mois" : `depuis ${months} mois`;
}

/**
 * Phrase courte « qui + pourquoi le relancer », derivee des vraies donnees du
 * contact. Calibree par type de signal, par ordre de fiabilite decroissante :
 *  1. un VRAI dernier echange en base -> sens + anciennete (« A repondu il y a 3j »).
 *  2. a defaut, l'anciennete factuelle du signal (payload.days_silent) : « Sans
 *     nouvelles depuis N ». On ne PRESUME jamais qui attend qui — au lendemain
 *     d'un import l'app ne connait pas encore le contact, donc zero invention.
 *  3. en dernier recours, une phrase de repli generique par type.
 * Fallback sur `signal.detail` (texte SQL) pour les types inconnus.
 */
export function buildSignalContextLine(
  signal: JulietteSignal,
  lastExchange?: LastExchangeSummary,
): string {
  const exchange = lastExchange ? buildExchangeSegment(lastExchange) : null;
  const days = exchange ? null : silentDays(signal);
  const silence = days !== null ? `Sans nouvelles ${formatSilence(days)}` : null;

  switch (signal.signalType) {
    case "hot_blocked":
      if (exchange) return `${exchange} Aucune relance prévue — à débloquer.`;
      if (silence) return `${silence} — à débloquer.`;
      return "Lead chaud sans relance prévue — à débloquer.";
    case "mandate_expiring":
      return "Mandat bientôt à échéance — proposer un point et la reconduction.";
    case "dormant":
      if (exchange) return `${exchange} Contact dormant à réveiller.`;
      if (silence) return `${silence} — à réveiller.`;
      return "Contact dormant à réveiller.";
    case "to_triage":
      if (exchange) return `${exchange} À trier : relancer, archiver ou laisser passer en dormant.`;
      if (silence) return `${silence} — à trier : relancer, archiver ou laisser passer.`;
      return "Pas de nouvelles depuis 1 à 3 mois — à toi de décider.";
    default:
      return signal.detail ?? "";
  }
}
